import React from "react";
import { observer } from "mobx-react-lite";
import { IconMobileDeviceLight, IconAlertRegular } from "@telefonica/mistica";

import { Box, Radio, RadioGroup, Stack } from "@chakra-ui/react";
import { ButtonFixedLayout, Chip, DataCard, ModalDisplayInfoCard } from "../../../../components";
import { useButtonPosition } from "../../../../utils/buttonPositionByScreenSize";

interface IProps {
	phoneNumbers: Array<{ number: string }>;
	isLoading: boolean;
	currentPhoneIndex: number;
	mustShowAlterLineModal: boolean;
	onSubmit(selectedPhoneNumber: string): Promise<void>;
}

const strings = {
	submitButton: "Continuar",
	choosePhoneCard: {
		title: "Validar Acesso",
		description:
        "Selecione um dos números abaixo para receber seu código de acesso por SMS",
	},
	lineChangeWarningModal: {
		title: "Você iniciou a contratação com outra linha",
		body: "Se preferir continuar com este número, você terá que enviar a sua documentação novamente",
		buttons: {
			primary: "Continuar",
			secondary: "Trocar número",
		},
	},
};


export const ChoosePhoneStep: React.FC<IProps> = observer((props) => {
	const {
		phoneNumbers,
		isLoading,
		onSubmit,
		currentPhoneIndex,
		mustShowAlterLineModal,
	} = props;
	const [ selectedNumber, setSelectedNumber ] = React.useState<string>();
	const [ showLineChangeWarningModal, setShowLineChangeWarningModal ] = React.useState<boolean>(false);
	const [ chipNumber, setChipNumber ] = React.useState<string>("");


	const formatObfuscatedPhone = (phone: string) => {
		if (phone.length === 11) {
			return `(${phone.substring(0, 2)}) ${phone.substring(2, 7)}-${phone.substring(7)}`;
		}
		return `(**) *****-${phone}`;
	};

	const onSubmitCallback = React.useCallback(
		() => {
			const mustShowModal = mustShowAlterLineModal && selectedNumber !== phoneNumbers[currentPhoneIndex].number;

			if (mustShowModal) {
				setChipNumber(formatObfuscatedPhone(selectedNumber!));
				return setShowLineChangeWarningModal(true);
			}

			if (selectedNumber) {
				onSubmit(selectedNumber);
			}
		},
		[selectedNumber, onSubmit],
	);

	const screenSizeLimit = 595;
	const buttonPosition = useButtonPosition(screenSizeLimit);

	return (
		<>
			<ButtonFixedLayout
				buttonStyles={{ position: buttonPosition }}
				primaryButton={{
					title: strings.submitButton,
					onClick: onSubmitCallback,
					disabled: !selectedNumber || isLoading,
				}}
				isFixed
			>
				<>
					<DataCard
						title={strings.choosePhoneCard.title}
						description={strings.choosePhoneCard.description}
					/>
					<Box
						w={{ base: "100%", lg: "66%" }}
						margin="0 auto"
						paddingTop={"40px"}
					>
						<RadioGroup
							name="phone-number"
							aria-labelledby="phone-numbers"
							value={selectedNumber}
							onChange={setSelectedNumber}
						>
							<Stack spacing={3}>
								{
									phoneNumbers.map(({number}) => (
										<label key={number} htmlFor={number} style={{
											border: "1px solid lightgray",
											width: "100%",
											padding: 24,
											borderRadius: 8,
											display: "grid",
											alignItems: "center",
											cursor: "pointer",
											gridTemplateColumns: "1fr 5fr 2fr",
										}}>
											<IconMobileDeviceLight /> {formatObfuscatedPhone(number)}
											<Radio
												value={number}
												id={number}
												key={number}
												display="flex"
												justifyContent="flex-end"
												colorScheme="purple"
											/>
										</label>
									))
								}
							</Stack>
						</RadioGroup>
					</Box>
					<ModalDisplayInfoCard
						description={strings.lineChangeWarningModal.body}
						displayInfo={strings.lineChangeWarningModal.title}
						primaryButtonText={strings.lineChangeWarningModal.buttons.primary}
						secondaryButtonText={strings.lineChangeWarningModal.buttons.secondary}
						chip={<Chip text={chipNumber}/>}
						handlePrimaryButton={() => {
							onSubmit(selectedNumber!);
						}}
						handleSecondaryButton={() => {
							setShowLineChangeWarningModal(false);
							setSelectedNumber("");
						}}
						isOpen={showLineChangeWarningModal}
						onClose={() => {
							setShowLineChangeWarningModal(false);
							setSelectedNumber("");
						}}
						icon={<IconAlertRegular />}
						isPrimaryButtonLoading={isLoading}
					/>
				</>
			</ButtonFixedLayout>
		</>
	);
});
